/**
 * Trade Impact Preview — pre-flight sanity strip.
 *
 * Renders a compact block beneath the SL/TP inputs so the trader can see the
 * TOTAL exposure across every connected account BEFORE clicking BUY/SELL.
 * Also warns loudly when the SL/TP distance is likely a decimal-mistake
 * (> 20 % of the base price in absolute-price mode).
 */
import { XCircle } from 'lucide-react';
import { CONTRACT_USD_PER_DOLLAR_MOVE } from './tradingHelpers';

export default function TradeImpactPreview({ order, connectedCount, livePrice, isAr }) {
  const symbol = String(order.symbol || '').toUpperCase();
  const volume = Number(order.volume) || 0;
  const totalVolume = volume * (connectedCount || 0);

  const isPending = order.mode === 'pending';
  const isBuy = isPending ? order.orderType.startsWith('buy') : true;
  const marketBase = livePrice ? (isBuy ? livePrice.ask : livePrice.bid) : null;
  const base = isPending
    ? (order.entryPrice ? Number(order.entryPrice) : null)
    : marketBase;

  // Resolve numeric SL/TP distances (absolute price → distance to base when
  // possible so we can estimate P&L in either input mode).
  const distFromInput = (kind) => {
    const v = order[kind];
    if (!v || Number(v) <= 0) return null;
    if (order[kind + 'Mode'] === 'distance') return Number(v);
    if (base && Number(v) > 0) return Math.abs(Number(v) - base);
    return null;
  };
  const slDist = distFromInput('sl');
  const tpDist = distFromInput('tp');

  const factor = CONTRACT_USD_PER_DOLLAR_MOVE[symbol];
  const riskPerAcc  = (slDist && factor) ? factor * slDist * volume : null;
  const rewardPerAcc = (tpDist && factor) ? factor * tpDist * volume : null;
  const totalRisk   = riskPerAcc  != null ? riskPerAcc  * (connectedCount || 0) : null;
  const totalReward = rewardPerAcc != null ? rewardPerAcc * (connectedCount || 0) : null;
  const rr = (riskPerAcc && rewardPerAcc && riskPerAcc > 0)
    ? rewardPerAcc / riskPerAcc
    : null;

  if (!connectedCount || !volume) return null;

  const showPnl = totalRisk != null || totalReward != null;

  // ── Sanity check: is either distance suspiciously large (>20% of base)? ──
  // Catches the classic mistake: user typed "3" into the Price-mode field for
  // XAUUSD (entry=4000) → distance becomes 3997 → warns the trader BEFORE
  // they click BUY.  Only fires when we have a base price to compare against.
  const dangerRatio = 0.20;
  const slDanger = base && slDist && (slDist / base) > dangerRatio;
  const tpDanger = base && tpDist && (tpDist / base) > dangerRatio;
  const anyDanger = slDanger || tpDanger;
  const priceModeSl = order.slMode === 'price' && slDanger;
  const priceModeTp = order.tpMode === 'price' && tpDanger;

  return (
    <div
      className="rounded-lg bg-white/[0.03] border border-white/10 p-2 space-y-1.5"
      data-testid="trade-impact-preview"
    >
      <div className="flex items-center justify-between text-[10px]">
        <span className="font-bold text-white/60">
          {isAr ? 'ملخّص التنفيذ' : 'Trade impact'}
        </span>
        <span className="font-mono text-white/80" dir="ltr">
          {volume.toFixed(2)} × {connectedCount} ={' '}
          <span className="text-[#d4af37] font-bold" data-testid="impact-total-volume">
            {totalVolume.toFixed(2)}
          </span>{' '}
          <span className="text-white/50">{isAr ? 'لوت' : 'lots'}</span>
        </span>
      </div>

      {/* Base price + distances — makes it crystal-clear what maths is happening */}
      {base != null && (slDist != null || tpDist != null) && (
        <div className="flex items-center justify-between text-[10px] text-white/60 font-mono pt-1 border-t border-white/5" dir="ltr">
          <span>
            {isAr ? 'سعر الأساس:' : 'Base:'}{' '}
            <span className="text-white font-bold">{Number(base).toFixed(5)}</span>
            <span className="text-white/40 text-[9px] mx-1">({isPending ? (isAr ? 'الدخول' : 'entry') : (isAr ? 'السوق' : 'live')})</span>
          </span>
          <span className="flex gap-2">
            {slDist != null && <span className="text-red-300">SL Δ {slDist.toFixed(2)}</span>}
            {tpDist != null && <span className="text-emerald-300">TP Δ {tpDist.toFixed(2)}</span>}
          </span>
        </div>
      )}

      {showPnl && (
        <div className="grid grid-cols-3 gap-2 pt-1 border-t border-white/5 text-[10px]">
          <div>
            <div className="text-white/40 mb-0.5">{isAr ? 'المخاطرة' : 'Risk'}</div>
            <div
              className={`font-mono font-bold ${totalRisk != null ? 'text-red-300' : 'text-white/30'}`}
              data-testid="impact-risk"
              dir="ltr"
            >
              {totalRisk != null ? `-$${totalRisk.toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-white/40 mb-0.5">{isAr ? 'الربح المتوقّع' : 'Reward'}</div>
            <div
              className={`font-mono font-bold ${totalReward != null ? 'text-emerald-300' : 'text-white/30'}`}
              data-testid="impact-reward"
              dir="ltr"
            >
              {totalReward != null ? `+$${totalReward.toFixed(2)}` : '—'}
            </div>
          </div>
          <div>
            <div className="text-white/40 mb-0.5">R:R</div>
            <div
              className={`font-mono font-bold ${
                rr == null
                  ? 'text-white/30'
                  : rr >= 2
                    ? 'text-emerald-300'
                    : rr >= 1
                      ? 'text-amber-300'
                      : 'text-red-300'
              }`}
              data-testid="impact-rr"
              dir="ltr"
            >
              {rr != null ? `1 : ${rr.toFixed(2)}` : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Danger banner: alerts user to a very likely input mistake */}
      {anyDanger && (
        <div
          className="rounded-md border border-red-500/50 bg-red-500/10 px-2 py-1.5 text-[10px] leading-tight"
          data-testid="impact-danger"
        >
          <div className="flex items-start gap-1.5">
            <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-px" />
            <div className="flex-1">
              <div className="text-red-300 font-black mb-0.5">
                {isAr ? '⚠ فارق ضخم — تحقّق قبل الإرسال!' : '⚠ Huge distance — verify before submit!'}
              </div>
              <div className="text-red-200/90">
                {isAr ? (
                  <>
                    {(priceModeSl || priceModeTp) ? (
                      <span>
                        الوضع الحالي <span className="font-bold">&laquo;سعر&raquo;</span> (رقم مطلق) ولكن الفارق &gt; 20% من الأساس.
                        <span className="block mt-0.5">
                          هل تقصد وضع <span className="text-[#d4af37] font-black">$</span> (مسافة بالدولار)؟
                        </span>
                      </span>
                    ) : (
                      <span>مسافة SL/TP كبيرة جداً مقارنة بالسعر ({Number((Math.max(slDist||0, tpDist||0)/base)*100).toFixed(0)}%). تحقّق من القيم.</span>
                    )}
                  </>
                ) : (
                  <>
                    {(priceModeSl || priceModeTp) ? (
                      <span>
                        Mode is <b>&ldquo;Price&rdquo;</b> (absolute) but the value is &gt; 20% away from the base.
                        <span className="block mt-0.5">
                          Did you mean <span className="text-[#d4af37] font-black">$</span> (distance) mode?
                        </span>
                      </span>
                    ) : (
                      <span>SL/TP distance is very large vs price ({Number((Math.max(slDist||0, tpDist||0)/base)*100).toFixed(0)}%). Double-check.</span>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!factor && (order.sl || order.tp) && (
        <div className="text-[9px] text-amber-300/70 pt-0.5" data-testid="impact-no-factor">
          {isAr
            ? '⚠ تقدير الأرباح/الخسائر غير متاح لهذا الرمز (رموز الفوركس)'
            : '⚠ P&L estimate unavailable for this symbol (FX pairs)'}
        </div>
      )}
      {!anyDanger && (
        <div className="text-[9px] text-white/30 pt-0.5">
          {isAr
            ? '* تقدير تقريبي، اعتماداً على مواصفات البروكر قد يختلف.'
            : '* Approximate — actual figures depend on broker specs.'}
        </div>
      )}
    </div>
  );
}
